import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  effect,
  inject,
  signal,
  type ElementRef,
  viewChild,
} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { PaymentDialogResult } from '../../shared/models/payment-dialog-result';
import { DialogResultType } from '../../shared/enums/dialogresulttype.enum';
import { PaymentService } from '../../shared/services/payment.service';
import { PaymentAccountType } from '../../shared/enums/paymentaccounttype.enum';
import { NotificationsService } from '../../shared/services/notifications.service';
import { NotificationType } from '../../shared/enums/notificationtype.enum';
import { NotificationComponent } from '../../shared/components/notification/notification.component';
import { StorageService } from '../../shared/services/storage.service';
import { StorageEnum } from '../../shared/enums/storage.enum';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { CmsService } from '../../shared/services/cms.service';
import { PaymentusService } from '../../shared/services/paymentus.service';
import type { Observable } from 'rxjs';
import { UtilService } from '../../shared/services/util.service';

@Component({
  selector: 'app-one-time-payment-pixel-modal',
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [AsyncPipe, NotificationComponent, MatButtonModule, MatDialogModule, MatInputModule],
  templateUrl: './one-time-payment-pixel-modal.component.html',
  styleUrl: './one-time-payment-pixel-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OneTimePaymentPixelModalComponent {
  readonly pageReady = signal(false);
  readonly tokenizationPixelRef = viewChild<ElementRef<HTMLElement>>('tokenizationPixel');
  readonly paymentusBase = viewChild<ElementRef<HTMLElement>>('paymentusBase');
  private readonly dialogRef = inject(MatDialogRef<OneTimePaymentPixelModalComponent>, { optional: true });
  private readonly destroyRef = inject(DestroyRef);
  private readonly paymentSvc = inject(PaymentService);
  private readonly notificationSvc = inject(NotificationsService);
  private readonly data = inject(MAT_DIALOG_DATA);
  private readonly storage = inject(StorageService);
  private readonly cms = inject(CmsService);
  private readonly paymentusSvc = inject(PaymentusService);
  private readonly utils = inject(UtilService);

  private tokenizationSuccessHandler: ((event: Event) => void) | null = null;
  private tokenizationErrorHandler: ((event: Event) => void) | null = null;
  private formStateHandler: ((event: Event) => void) | null = null;
  private tokenizationPaymentMethodChangedHandler: ((event: Event) => void) | null = null;
  private tokenExpiringHandler: ((event: Event) => void) | null = null;
  public isFormValid = false;

  private readonly tokenizationListenerEffect = effect(() => {
    const tokenizationPixel = this.tokenizationPixelRef()?.nativeElement;
    const paymentusBase = this.paymentusBase()?.nativeElement;

    if (!tokenizationPixel || !paymentusBase) {
      return;
    }

    // Assign after upgrade so the custom element's own property accessor is in place.
    (tokenizationPixel as unknown as { availablePaymentMethods: string[] }).availablePaymentMethods =
      this.paymentMethods;

    if (this.tokenizationSuccessHandler) {
      return;
    }

    this.tokenizationSuccessHandler = (event: Event): void => {
      const customEvent = event as CustomEvent;
      // save data and close modal
      const data = customEvent.detail?.data;
      const normalizedData = data?.['profile-response'] ? data : { 'profile-response': data };
      const type = normalizedData['profile-response']?.type;
      if (type === 'CHQ' || type === 'SAV' || type === 'BUS_CHQ') {
        this.saveBankPaymentData(normalizedData);
      } else {
        this.saveCardPaymentData(normalizedData);
      }
    };

    tokenizationPixel.addEventListener('TOKENIZATION_SUCCESS', this.tokenizationSuccessHandler);

    if (!this.tokenizationErrorHandler) {
      this.tokenizationErrorHandler = (event: Event): void => {
        this.handleTokenizationError(event as CustomEvent);
      };

      tokenizationPixel.addEventListener('TOKENIZATION_ERROR', this.tokenizationErrorHandler);
    }

    if (!this.formStateHandler) {
      this.formStateHandler = (event: Event): void => {
        const customEvent = event as CustomEvent;
        this.isFormValid = customEvent.detail?.formValid;
      };

      tokenizationPixel.addEventListener('FORM_STATE', this.formStateHandler);
    }

    this.tokenizationPaymentMethodChangedHandler = (): void => {
      this.notificationSvc.clearAllNotifications(this.pageName);
    };

    tokenizationPixel.addEventListener(
      'TOKENIZATION_PAYMENT_METHOD_CHANGED',
      this.tokenizationPaymentMethodChangedHandler,
    );

    if (!this.tokenExpiringHandler) {
      this.tokenExpiringHandler = (event: Event): void => {
        const customEvent = event as CustomEvent<{ expiresAt?: string }>;
        const { expiresAt } = customEvent.detail;

        if (!expiresAt) {
          return;
        }

        this.paymentusSvc.getPaymentusTokenPixel().subscribe({
          next: (token: string) => {
            if (this.utils.isNullOrWhitespace(token)) {
              const newPaymentsErrorMessage = this.paymentusErrors.find(
                (error: { paymentusError?: { errorCode?: string } }) => {
                  return error.paymentusError?.errorCode === '90002-sdk-error';
                },
              );
              this.showFormErrorLabel(newPaymentsErrorMessage?.paymentusError?.displayMessage);
              return;
            }
            if (token.trim()) {
              paymentusBase.setAttribute('authorization', token);
            }
          },
          error: () => {
            const newPaymentsErrorMessage = this.paymentusErrors.find(
              (error: { paymentusError?: { errorCode?: string } }) => {
                return error.paymentusError?.errorCode === '90002-sdk-error';
              },
            );
            const errorMessage = newPaymentsErrorMessage?.paymentusError?.displayMessage ?? '';
            this.showFormErrorLabel(errorMessage);
            paymentusBase.removeAttribute('authorization');
            const dialogResult = new PaymentDialogResult();
            dialogResult.Type = DialogResultType.Error;
            dialogResult.ErrorMessage = errorMessage;
            this.dialogRef?.close(dialogResult);
          },
        });
      };

      paymentusBase.addEventListener('TOKEN_EXPIRING', this.tokenExpiringHandler);
    }

    this.destroyRef.onDestroy(() => {
      if (this.tokenizationSuccessHandler) {
        tokenizationPixel.removeEventListener('TOKENIZATION_SUCCESS', this.tokenizationSuccessHandler);
      }
      if (this.tokenizationErrorHandler) {
        tokenizationPixel.removeEventListener('TOKENIZATION_ERROR', this.tokenizationErrorHandler);
      }
      if (this.formStateHandler) {
        tokenizationPixel.removeEventListener('FORM_STATE', this.formStateHandler);
      }
      if (this.tokenizationPaymentMethodChangedHandler) {
        tokenizationPixel.removeEventListener(
          'TOKENIZATION_PAYMENT_METHOD_CHANGED',
          this.tokenizationPaymentMethodChangedHandler,
        );
      }
      if (this.tokenExpiringHandler) {
        paymentusBase.removeEventListener('TOKEN_EXPIRING', this.tokenExpiringHandler);
      }
    });
  });

  public pixelJwtToken: string | null = null;
  public paymentMethods: string[] = [];
  public limitedPaymentOptions: true | false = false;
  public pageName = 'OneTimePaymentPixelForm';
  public formErrorMessage: string = '';
  public paymentusErrors = this.storage.getSession(StorageEnum.GlobalVars)?.paymentusErrors;
  public pageLabels$!: Observable<any>;

  ngOnInit(): void {
    this.pageLabels$ = this.cms.getContent('paymethod-pixel-modal', 'PaymethodPixelModal');
    this.pixelJwtToken = this.data.pixelJwtToken;
    this.limitedPaymentOptions = this.data.isLimitedPaymentOptions;
    this.paymentMethods = this.limitedPaymentOptions ? ['card'] : ['card', 'ach'];
    this.pageReady.set(true);
  }

  closeModal(): void {
    const dialogResult = new PaymentDialogResult();
    dialogResult.Type = 'Cancel';
    dialogResult.PayMethod.ReferenceId = '';
    if (this.dialogRef) {
      this.dialogRef.close(dialogResult);
    }
  }

  private saveCardPaymentData(data: any): void {
    const dialogResult = new PaymentDialogResult();
    dialogResult.Type = DialogResultType.Add;
    dialogResult.PayMethod.ReferenceId = data['profile-response'].token;
    dialogResult.PayMethod.Nickname = '';
    const expiryDate = data['profile-response']['credit-card-expiry-date'];
    dialogResult.PayMethod.ExpDate = `${expiryDate.month}${expiryDate.year}`;
    dialogResult.PayMethod.IsDefault = false;
    dialogResult.PayMethod.SaveToAccount = false;
    dialogResult.PayMethod.LastFour = data['profile-response']['account-number'].replaceAll('*', '');
    dialogResult.PayMethod.PaymentAccountType = this.getMappedPaymentType(data['profile-response'].type);
    dialogResult.PayMethod.Description = this.paymentSvc.getPaymethodDescription(
      dialogResult.PayMethod.PaymentAccountType,
      dialogResult.PayMethod.LastFour,
    );
    dialogResult.PayMethod.BankCardDigital = 'CARD';
    const fullName: string = data['profile-response']['card-holder-name'] ?? '';
    const firstSpaceIndex = fullName.indexOf(' ');
    const firstName = firstSpaceIndex === -1 ? fullName : fullName.slice(0, firstSpaceIndex);
    const lastName = firstSpaceIndex === -1 ? '' : fullName.slice(firstSpaceIndex + 1).trim();
    dialogResult.OneTimePaymentFirstName = firstName;
    dialogResult.OneTimePaymentLastName = lastName;
    if (this.dialogRef) {
      this.dialogRef.close(dialogResult);
    }
  }

  private saveBankPaymentData(data: any): void {
    const dialogResult = new PaymentDialogResult();
    dialogResult.Type = DialogResultType.Add;
    dialogResult.PayMethod.ReferenceId = data['profile-response'].token;
    dialogResult.PayMethod.BankAccountType = data['profile-response'].type;
    dialogResult.PayMethod.Nickname = '';
    dialogResult.PayMethod.IsDefault = false;
    dialogResult.PayMethod.SaveToAccount = false;
    let lastFour = data['profile-response']['account-number'];
    lastFour = lastFour.slice(lastFour.indexOf('-') + 1).replaceAll('*', '');
    dialogResult.PayMethod.LastFour = lastFour;
    dialogResult.PayMethod.PaymentAccountType = PaymentAccountType.Banking;
    dialogResult.PayMethod.Description = this.paymentSvc.getPaymethodDescription(
      dialogResult.PayMethod.PaymentAccountType,
      dialogResult.PayMethod.LastFour,
    );
    dialogResult.PayMethod.BankCardDigital = 'BANK';
    const fullName: string = data['profile-response']['card-holder-name'] ?? '';
    const firstSpaceIndex = fullName.indexOf(' ');
    const firstName = firstSpaceIndex === -1 ? fullName : fullName.slice(0, firstSpaceIndex);
    const lastName = firstSpaceIndex === -1 ? '' : fullName.slice(firstSpaceIndex + 1).trim();
    dialogResult.OneTimePaymentFirstName = firstName;
    dialogResult.OneTimePaymentLastName = lastName;
    if (this.dialogRef) {
      this.dialogRef.close(dialogResult);
    }
  }

  private handleTokenizationError(event: CustomEvent): void {
    const genericPaymentsErrorMessage = this.paymentusErrors.find(
      (error: { paymentusError?: { errorCode?: string } }) => {
        return error.paymentusError?.errorCode === '90001-generic-error';
      },
    );
    const formErrorMessage = this.storage.getSession(StorageEnum.GlobalVars).formError;
    const detail = event.detail;
    const errorData = detail?.data;
    // Session timeout error
    if (errorData?.status === 403) {
      this.showFormErrorLabel(genericPaymentsErrorMessage?.paymentusError?.displayMessage);
      return;
    }

    // Standard errors
    if (errorData?.error?.errors) {
      const errors = errorData.error.errors;

      errors.forEach((error: { key: string; value: unknown }) => {
        switch (error.key) {
          // Duplicate profile detected. User may need to select existing profile.
          case 'xmlCreateProfReq.duplicateProfile':
            this.showFormErrorLabel(genericPaymentsErrorMessage?.paymentusError?.displayMessage);
            break;
          // Invalid credit card number provided.
          case 'pmCreditCardNumber.invalid':
            this.showFormErrorLabel(formErrorMessage);
            break;
          // Card has expired.
          case 'pmExpiryDate.past':
            this.showFormErrorLabel(formErrorMessage);
            break;
          // Database error occurred. Please try again.
          case 'xmlProfile.notSaved':
            this.showFormErrorLabel(genericPaymentsErrorMessage?.paymentusError?.displayMessage);
            break;
          // Invalid credit card verification code length.
          case 'pmCreditCardVerification.length':
            this.showFormErrorLabel(formErrorMessage);
            break;
          // Invalid credit card verification code provided.
          case 'pmCreditCardVerification.invalid':
            this.showFormErrorLabel(formErrorMessage);
            break;
          // Invalid group type.
          case 'groupType.invalid':
            this.showFormErrorLabel(genericPaymentsErrorMessage?.paymentusError?.displayMessage);
            break;
          // Payment method type is empty.
          case 'paymentMethodType.empty':
            this.showFormErrorLabel(genericPaymentsErrorMessage?.paymentusError?.displayMessage);
            break;
          // Invalid payment method expiry date.
          case 'pmExpiryDate.invalid':
            this.showFormErrorLabel(formErrorMessage);
            break;
          // First name is empty.
          case 'firstName.empty':
            this.showFormErrorLabel(formErrorMessage);
            break;
          // Last name is empty.
          case 'lastName.empty':
            this.showFormErrorLabel(formErrorMessage);
            break;
          // XML profile not supported.
          case 'xmlProfile.notSupported':
            this.showFormErrorLabel(genericPaymentsErrorMessage?.paymentusError?.displayMessage);
            break;
          default:
            // Unknown error occurred.
            this.showFormErrorLabel(genericPaymentsErrorMessage?.paymentusError?.displayMessage);
        }
      });
    } else {
      // Unexpected error format
      const newPaymentsErrorMessage = this.paymentusErrors.find(
        (error: { paymentusError?: { errorCode?: string } }) => {
          return error.paymentusError?.errorCode === '90002-sdk-error';
        },
      );
      const dialogResult = new PaymentDialogResult();
      dialogResult.Type = DialogResultType.Error;
      dialogResult.ErrorMessage = newPaymentsErrorMessage.paymentusError?.displayMessage; // message;
      if (this.dialogRef) {
        this.dialogRef.close(dialogResult);
      }
    }
  }

  getMappedPaymentType(cardValue: string): PaymentAccountType {
    const card = cardValue.toLowerCase();
    if (card === 'mc') {
      return PaymentAccountType.MasterCard;
    } else if (card === 'amex') {
      return PaymentAccountType.AmericanExpress;
    } else if (card === 'disc') {
      return PaymentAccountType.Discover;
    } else if (card === 'visa') {
      return PaymentAccountType.Visa;
    } else {
      return PaymentAccountType.Unknown;
    }
  }

  showFormErrorLabel(errorMessage: string): void {
    this.formErrorMessage = errorMessage;
    if (this.formErrorMessage && this.formErrorMessage !== '') {
      this.notificationSvc.clearAllNotifications(this.pageName);
      this.notificationSvc.addNotification(this.formErrorMessage, NotificationType.Error, this.pageName);
    }
  }

  onSaveClick(): void {
    const tokenizationPixel = this.tokenizationPixelRef()?.nativeElement as
      | (HTMLElement & { tokenize: (callback: (response: { error?: unknown; token?: string }) => void) => void })
      | undefined;

    if (!tokenizationPixel) {
      return;
    }

    tokenizationPixel.tokenize((response) => {
      if (!this.isFormValid) {
        const errorMessage = this.storage.getSession(StorageEnum.GlobalVars).formError;
        this.showFormErrorLabel(errorMessage);
        return;
      }
      if (response.error) {
        this.showFormErrorLabel(this.storage.getSession(StorageEnum.GlobalVars).formError);
      }
    });
  }
}
